/**
 * General helper functions for the application
 */

// Date formatting helpers
export const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleDateString();
};

export const formatDateTime = (dateString) => {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleString();
};

// Duration formatting helpers (duration stored as integer minutes)
export const formatDuration = (duration) => {
  if (!duration && duration !== 0) return 'Duration not specified';
  const mins = parseInt(duration, 10);
  if (isNaN(mins)) return 'Duration not specified';
  return `${mins} min${mins !== 1 ? 's' : ''}`;
};

// Status helpers
export const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'Completed': return 'success';
    case 'In Progress': return 'warning';
    case 'Not Started': return 'secondary';
    default: return 'secondary';
  }
};

export const getStatusIcon = (status) => {
  switch (status) {
    case 'Completed': return 'fas fa-check-circle';
    case 'In Progress': return 'fas fa-clock';
    case 'Not Started': return 'fas fa-play-circle';
    default: return 'fas fa-play-circle';
  }
};

// Image helpers
export const getSampleImage = (moduleName) => {
  const colors = ['#007bff', '#28a745', '#ffc107', '#B32025', '#6f42c1', '#fd7e14'];
  const color = colors[moduleName.length % colors.length];
  return `https://via.placeholder.com/300x200/${color.substring(1)}/ffffff?text=${encodeURIComponent(moduleName)}`;
};

// Progress calculation helpers
export const calculateProgress = (modules, getModuleStatus) => {
  if (modules.length === 0) return 0;
  const completedModules = modules.filter(module => getModuleStatus(module.id) === 'Completed').length;
  return Math.round((completedModules / modules.length) * 100);
};

// Validation helpers
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPassword = (password) => {
  return password && password.length >= 6;
};

// String helpers
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Array helpers
export const sortBySequence = (items) => {
  return items.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};

export const groupBy = (array, key) => {
  return array.reduce((groups, item) => {
    const group = item[key];
    groups[group] = groups[group] || [];
    groups[group].push(item);
    return groups;
  }, {});
};

// Object helpers
export const isEmpty = (obj) => {
  return obj === null || obj === undefined || 
         (typeof obj === 'object' && Object.keys(obj).length === 0);
};

export const pick = (obj, keys) => {
  return keys.reduce((result, key) => {
    if (obj.hasOwnProperty(key)) {
      result[key] = obj[key];
    }
    return result;
  }, {});
};

// URL helpers
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const getUrlDomain = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

// Local storage helpers
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting localStorage:', error);
  }
};

export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error getting localStorage:', error);
    return defaultValue;
  }
};

export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage:', error);
  }
};

// Debounce helper
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle helper
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}; 